/**
 * Centralized translations for Hand Hygiene module
 * Contains all labels used across hand hygiene components
 */

export const handHygieneLabels = {
    it: {
        // Main form labels
        handHygiene: `Scheda Igiene Mani`,
        healthAuthority: 'Autorità Sanitaria',
        departmentCategory: 'Categoria dipartimento',
        lastModifyDate: 'Data ultima modifica',
        department: 'Dipartimento',
        functionalArea: 'U.O./Area funzionale',
        observer: 'Osservatore',
        sessionLength: 'Durata sessione (min)',
        notes: 'Note',
        date: 'Data',
        startEnd: `Ora di inizio e fine osservazione`,
        notesPlaceholder: 'Scrivi le tue note qui',
        setCurrentTime: 'Imposta data e ora corrente',
        
        // Actions and operations
        savingData: 'Stiamo salvando i tuoi dati',
        attention: 'Attenzione',
        success: 'Tutto OK',
        close: 'Chiudi',
        save: 'Salva',
        delete: "Elimina",
        cancel: "Annulla",
        
        // Success/Error messages
        handHygieneSavedSuccesfully: 'Scheda igiene mani salvata con successo',
        handHygieneSavingError: 'Errore nella salvataggio della scheda igiene mani',
        handHygieneDeletedSuccessfully: "Scheda eliminata con successo",
        error: `Errore`,
        nothingToSave: 'Niente da salvare',
        
        // Validation messages
        departmentTypeRequired: `La tipologia del reparto non è stata selezionata`,
        startDateTimeRequired: `L'orario di inizio dell'osservazione non è stato selezionato`,
        endDateTimeRequired: `L'orario di fine dell'osservazione non è stato selezionato`,
        endDateTimeBeforeStart: `L'orario di fine non può essere precedente a quello di inizio`,
        departmentRequired: `Il campo Dipartimento è obbligatorio`,
        functionalAreaRequired: `Il campo Area funzionale è obbligatorio`,
        observedOperatorType: `Il campo tipo nell'OPP {{opp}} è obbligatorio`,
        opportunityMissingOperator: `Ci sono opportunità nell'OPP {{opp}} ma manca l'operatore`,
        incompleteOpportunity: `L'OPP {{oppIndex}} dell'operatore {{operatorOrder}} è incompleta: devi selezionare sia l'indicazione che l'azione`,
        missingOpportunityType: `L'OPP {{oppIndex}} dell'operatore {{operatorOrder}} è incompleta: devi selezionare il tipo`,
        missingGenderType: `L'OPP {{oppIndex}} dell'operatore {{operatorOrder}} è incompleta: devi selezionare il genere`,
        noOpportunitiesWithMainData: `Sono stati compilati i dati principali ma non è stata aggiunta nessuna opportunità osservata`,
        
        // Sheet and navigation
        handHygieneSheet: 'Scheda Igiene Mani : {{identifier}}',
        index: `Indice`,
        areYouSure: "Sei sicuro di voler eliminare questa scheda?",
        
        // Hygiene indications
        prContExp: ` : prima di un contatto con il paziente`,
        prAsepsExp: ` : prima di una manovra in asepsi`,
        dpFluidoExp: ` : dopo esposizione a rischio con un fluido corporeo`,
        dpContExp: ` : dopo un contatto con il paziente`,
        dpCircExp: ` : dopo un contatto con ciò che circonda il paziente`,
        indicationMeaning: `Significato delle indicazioni :`,
        
        // Navigation and prevention
        unsavedChanges: "Ci sono modifiche non salvate. Vuoi davvero uscire senza salvare?",
        exitWithoutSaving: "Sì, esci senza salvare",
        
        // Access control
        accessDenied: "Accesso negato",
        youDoNotHavePermissionToModify: "Non hai i permessi per modificare questa scheda",
        youAreNotTheSheetOwner: "Non sei il proprietario della scheda",
        
        // Observed Opportunity labels
        profCategory: `Categoria Professionale`,
        code: `Codice`,
        type: `Tipo`,
        gender: `Sesso`,
        count: `Conta`,
        addOpportunity: `Aggiungi Opportunità`,
        deletingMessage: `Sei sicuro di voler eliminare l'OPP {{opp}} ?`,
        handHygieneAction: 'Azione Igiene Mani',
        indication: 'Indicazione',
        deleted: 'Eliminato!',
        deletedSuccesfully: `L'opportunità è stata rimossa.`,
        yesDelete: 'Sì, elimina',
        cancel: 'Annulla',
        
        // Opportunities labels
        befPat: 'pr-cont.',
        befAsept: 'pr-aseps.',
        aftBF: 'dp-fluido',
        aftPat: 'dp-cont.',
        aftPSurr: 'dp-circ.',
        
        // Actions labels
        hr: 'frizione',
        hw: 'lavaggio',
        missed: 'nessuna',
        gloves: 'guanti',
        
        // Type labels
        internal: 'Interno',
        external: 'Esterno',
        
        // Gender labels
        man: 'Uomo',
        woman: 'Donna',
        
        // Finder specific labels
        handHygieneDataFinder: 'Ricerca Dati Igiene delle Mani',
        customDepartmentCategory: 'Categoria custom per il reparto',
        startDate: `Periodo di inizio`,
        endDate: `Periodo di fine`,
        id: `Id`,
        search: 'Cerca',
        clear: 'Cancella',
        yes: `Si`,
        no: `No`,
        acpm: `Acpm`,
        note: `Note`,
        opp: `Opp`,
        pageRows: `Righe per pagina:`,
        backPage: `Precedente`,
        nextPage: `Successivo`,
        pageOf: `Pagina {{currentPage}} di {{totalPages}}`,
        findedElements: `Risultati della ricerca`
    },
    
    en: {
        // Main form labels
        handHygiene: `Hand Hygiene Sheet`,
        healthAuthority: 'Health Authority',
        departmentCategory: "Department Category",
        lastModifyDate: 'Last modify date',
        department: 'Department',
        functionalArea: 'OU/Functional Area',
        observer: 'Observer',
        sessionLength: 'Session length (min)',
        notes: 'Notes',
        date: 'Date',
        startEnd: `Start and End Time`,
        notesPlaceholder: 'Write your notes here',
        setCurrentTime: 'Set current date and time',
        
        // Actions and operations
        savingData: 'We are saving your data',
        attention: 'Attention',
        success: 'Well Done',
        close: 'Close',
        save: 'Save',
        delete: "Delete",
        cancel: "Cancel",
        
        // Success/Error messages
        handHygieneSavedSuccesfully: 'Hand Hygiene Sheet saved successfully',
        handHygieneSavingError: 'Error saving Hand Hygiene Sheet',
        handHygieneDeletedSuccessfully: "Sheet deleted successfully",
        error: 'Error',
        nothingToSave: `Nothing to save`,
        
        // Validation messages
        departmentTypeRequired: "Department type not selected",
        startDateTimeRequired: `Start Time needs to be selected`,
        endDateTimeRequired: `End Time needs to be selected`,
        endDateTimeBeforeStart: `End Time cannot be earlier than Start Time`,
        departmentRequired: `Department is a mandatory field`,
        functionalAreaRequired: `Functional area is a mandatory field`,
        observedOperatorType: `Type in OPP {{opp}} is a mandatory field`,
        opportunityMissingOperator: `Opportunities are defined in OPP {{opp}} but no operator is assigned`,
        incompleteOpportunity: `OPP {{oppIndex}} of operator {{operatorOrder}} is incomplete: must select both indication and hand hygiene action`,
        missingOpportunityType: `OPP {{oppIndex}} of operator {{operatorOrder}} is incomplete: must select OPP's type`,
        missingGenderType: `OPP {{oppIndex}} of operator {{operatorOrder}} is incomplete: must select OPP's gender`,
        noOpportunitiesWithMainData: `Main data has been filled but no observed opportunities have been added`,
        
        // Sheet and navigation
        handHygieneSheet: 'Hand Hygiene Sheet : {{identifier}}',
        index: `Index`,
        areYouSure: "Are you sure you want to delete this sheet?",
        
        // Hygiene indications
        prContExp: ` : before patient contact`,
        prAsepsExp: ` : before an aseptic procedure`,
        dpFluidoExp: ` : after exposure to body fluid risk`,
        dpContExp: ` : after patient contact`,
        dpCircExp: ` : after contact with patient surroundings`,
        indicationMeaning: `Meaning of the indication :`,
        
        // Navigation and prevention
        unsavedChanges: "There are unsaved changes. Do you really want to exit without saving?",
        exitWithoutSaving: "Yes, exit without saving",
        
        // Access control
        accessDenied: "Access denied",
        youDoNotHavePermissionToModify: "You do not have permission to modify this sheet",
        youAreNotTheSheetOwner: "You are not the sheet owner",
        
        // Finder specific labels
        handHygieneDataFinder: 'Hand Hygiene Data Finder',
        customDepartmentCategory: 'Custom category for department',
        
        // Observed Opportunity labels  
        profCategory: `Profession Category`,
        code: `Code`,
        type: `Type`,
        gender: `Gender`,
        count: `Count`,
        addOpportunity: `Add Opportunity`,
        deletingMessage: `Are you sure that you want to delete the OPP {{opp}} ?`,
        handHygieneAction: 'Hand Hygiene Action',
        indication: 'Indication',
        deleted: 'Deleted!',
        deletedSuccesfully: 'Opportunity deleted successfully',
        yesDelete: 'Yes, delete',
        cancel: 'Cancel',
        
        // Opportunities labels
        befPat: 'bef-pat.',
        befAsept: 'bef-asept.',
        aftBF: 'aft-b-f',
        aftPat: 'aft-pat.',
        aftPSurr: 'aft-p-surr.',
        
        // Actions labels
        hr: 'hand rub',
        hw: 'hand wash',
        missed: 'missed',
        gloves: 'gloves',
        
        // Type labels
        internal: 'Internal',
        external: 'External',
        
        // Gender labels
        man: 'Man',
        woman: 'Woman',
        
        // Finder specific labels
        customDepartmentCategory: 'Custom Department Category',
        startDate: 'Start date',
        endDate: `End date`,
        id: `Id`,
        search: 'Search',
        clear: 'Clear',
        yes: `Yes`,
        no: `No`,
        acpm: `Acpm`,
        note: `Notes`,
        opp: `Opp`,
        pageRows: `Page rows:`,
        backPage: `Next`,
        nextPage: `Back`,
        pageOf: `Page {{currentPage}} of {{totalPages}}`,
        findedElements: `Found Elements`
    }
}; 